import React from 'react';
import {
  Tooltip as MuiTooltip,
  TooltipProps as MuiTooltipProps,
  Fade,
  Grow,
  Slide,
  Zoom,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledTooltip = styled(MuiTooltip, {
  shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant?: 'default' | 'light' | 'dark' | 'error' | 'warning' | 'info' | 'success' }>(
  ({ theme, variant = 'default' }) => {
    const getVariantStyles = () => {
      switch (variant) {
        case 'light':
          return {
            backgroundColor: theme.palette.common.white,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[2],
          };
        case 'dark':
          return {
            backgroundColor: theme.palette.grey[800],
            color: theme.palette.common.white,
          };
        case 'error':
          return {
            backgroundColor: theme.palette.error.main,
            color: theme.palette.error.contrastText,
          };
        case 'warning':
          return {
            backgroundColor: theme.palette.warning.main,
            color: theme.palette.warning.contrastText,
          };
        case 'info':
          return {
            backgroundColor: theme.palette.info.main,
            color: theme.palette.info.contrastText,
          };
        case 'success':
          return {
            backgroundColor: theme.palette.success.main,
            color: theme.palette.success.contrastText,
          };
        default:
          return {};
      }
    };

    return {
      '& .MuiTooltip-tooltip': {
        ...getVariantStyles(),
        fontSize: theme.typography.pxToRem(12),
        fontWeight: 500,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(0.75, 1.5),
        maxWidth: 300,
      },
      '& .MuiTooltip-arrow': {
        color: variant === 'light' ? theme.palette.common.white : undefined,
      },
    };
  }
);

export interface TooltipProps extends Omit<MuiTooltipProps, 'children'> {
  /**
   * The content of the component
   */
  children: React.ReactElement;
  /**
   * The tooltip content
   */
  title: React.ReactNode;
  /**
   * Visual variant of the tooltip
   */
  variant?: 'default' | 'light' | 'dark' | 'error' | 'warning' | 'info' | 'success';
  /**
   * Animation variant
   */
  animation?: 'fade' | 'grow' | 'slide' | 'zoom';
  /**
   * Whether to show an arrow
   */
  arrow?: boolean;
  /**
   * Delay before showing tooltip (ms)
   */
  enterDelay?: number;
  /**
   * Delay before hiding tooltip (ms)
   */
  leaveDelay?: number;
  /**
   * Whether tooltip is interactive (hoverable)
   */
  interactive?: boolean;
}

const getTransitionComponent = (animation: string) => {
  switch (animation) {
    case 'grow':
      return Grow;
    case 'slide':
      return Slide;
    case 'zoom':
      return Zoom;
    case 'fade':
    default:
      return Fade;
  }
};

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  title,
  variant = 'default',
  animation = 'fade',
  arrow = false,
  enterDelay = 500,
  leaveDelay = 0,
  interactive = false,
  placement = 'top',
  ...props
}) => {
  const TransitionComponent = getTransitionComponent(animation);

  return (
    <StyledTooltip
      title={title}
      variant={variant}
      arrow={arrow}
      enterDelay={enterDelay}
      leaveDelay={leaveDelay}
      disableInteractive={!interactive}
      placement={placement}
      TransitionComponent={TransitionComponent}
      {...props}
    >
      {children}
    </StyledTooltip>
  );
}; 